/* eslint-disable sort-keys */
import { useEffect, useState } from 'react'

import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material'

import {
  not,
  isNil,
  propEq,
  prop,
  path,
  find,
  and,
  gt,
  defaultTo,
  length
} from 'ramda'
import useCurrentDomain from 'hooks/useCurrentDomain'
import {
  useGetPublicSubdomainQuery,
  useGetPublicUserDomainsQuery
} from 'api/publicApi'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHandlePublicRoutes } from 'hooks'
import { useParams } from 'react-router-dom'

import PollCard from 'pages/public/components/cards/PollCard'
import ResourceBlock from './components/ResourceBlock'
import CTASection from 'pages/public/components/CTASection'
import RecommendedSubdomains from './components/RecommendedSubdomains'
import BreadCrumbs from 'pages/public/components/BreadCrumbs'
import LearnNewSkillsBlock from 'pages/subdomain/components/LearnNewSkillsBlock'
import { useSelector } from 'react-redux'

const Subdomain = () => {
  useHandlePublicRoutes()
  const { currentDomain } = useCurrentDomain()
  // const { locale } = useRouter()
  const { domain, subdomain } = useParams()
  const intl = useIntl()
  const [subdomainId, setSubdomainId] = useState(null)
  const school = useSelector(prop('school'))
  const org_domain = prop('domain', school)

  const isTablet = useMediaQuery('(max-width: 900px)')

  const {
    data: userDomains,
    fulfilledTimeStamp: hasFetchedDomains,
    refetch: refetchUserDomains
  } = useGetPublicUserDomainsQuery(currentDomain)

  useEffect(() => {
    if (not(isNil(userDomains))) {
      const foundDomain = find(propEq(domain, 'frontend_url_slug'), userDomains)
      setSubdomainId(
        prop(
          'id',
          find(
            propEq(subdomain, 'frontend_url_slug'),
            prop('subdomains', foundDomain)
          )
        )
      )
    }
  }, [userDomains, subdomain])

  const {
    data,
    fulfilledTimeStamp: hasFetchedSudomain,
    refetch: refetchSubdomains
  } = useGetPublicSubdomainQuery(
    { id: subdomainId, org: org_domain },
    {
      skip: isNil(subdomainId)
    }
  )

  const hasSkillCards = gt(defaultTo(0, length(prop('skill_cards', data))), 0)

  useEffect(() => {
    // If the fulfilled timestamps do exist and the locale is changed
    // refetch the domain and subdomain data so we can see translated content
    if (and(not(isNil(hasFetchedSudomain)), not(isNil(hasFetchedDomains)))) {
      refetchUserDomains()
      refetchSubdomains()
    }
    // }, [locale])
  }, [])

  const hasPoll = path(['poll_question', 'allow_as_poll_on_subdomain'], data)

  if (isNil(data)) return null

  return (
    <Stack component="article" id="main" spacing={1}>
      <Box
        sx={{
          background: `url(${path(['subdomain', 'hero_image_url'], data)})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          height: 365
        }}
        width="100%"
      />
      <Box>
        <Container>
          <Box
            sx={{
              display: 'grid',
              gridGap: { xs: 16, md: 32 },
              gridTemplateColumns: {
                xs: '1fr',
                md: hasPoll ? '1fr 350px' : '1fr'
              },
              mb: { xs: 4, md: hasPoll ? 0 : 3 }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gridGap: 16
              }}
            >
              <BreadCrumbs
                links={[
                  {
                    href: '/',
                    label: intl.formatMessage({
                      defaultMessage: 'Explore',
                      id: '7JlauX'
                    })
                  },
                  {
                    href: `/${domain}/${subdomain}`,
                    label: path(['subdomain', 'frontend_name'], data)
                  }
                ]}
              />
              <Typography variant="h1">
                <FormattedMessage
                  defaultMessage="{title}"
                  id="2YgLVn"
                  values={{
                    title: path(['subdomain', 'onboarding_title'], data)
                  }}
                />
              </Typography>
              <Card>
                <Divider color="school.primary" variant="card accent" />
                <CardContent>
                  <Typography variant="body1">
                    <FormattedMessage
                      defaultMessage="{content}"
                      id="zb24ln"
                      values={{
                        content: path(['subdomain', 'context_copy'], data)
                      }}
                    />
                  </Typography>
                </CardContent>
              </Card>
              {hasSkillCards && (
                <Box sx={{ gridArea: 'skills' }}>
                  <LearnNewSkillsBlock
                    containsPolling={hasPoll}
                    isPublic={true}
                    skillCards={prop('skill_cards', data)}
                  />
                </Box>
              )}
            </Box>
            {hasPoll && (
              <Box sx={{ pt: isTablet ? 0 : 15.5 }}>
                <PollCard content={prop('poll_question', data)} />
              </Box>
            )}
          </Box>
          <ResourceBlock {...prop('subdomain', data)} />
          <RecommendedSubdomains subdomainData={prop('subdomain', data)} />
        </Container>
        <CTASection />
      </Box>
    </Stack>
  )
}

export default Subdomain
